import {
  PIE,
  DOUGHNUT,
  VERTICAL_BAR,
  LINE,
  STACKED_VERTICAL_BAR,
  GROUPED_VERTICAL_BAR,
  MULTILINE,
  NUMBER,
  SUMMARY_TABLE,
  GROUPED_HORIZONTAL_BAR,
  STACKED_HORIZONTAL_BAR,
  HORIZONTAL_BAR
} from '@nile/reports/src/modules/charts/constants';
import { isEmpty, omit } from 'lodash';
import { getTranslatedText } from '@nile/components';
import {
  getPieOptions,
  getBarOptions,
  getLineOptions,
  getCustomChartOptions,
  getStackedBarOptions,
  getGroupedBarOptions,
  getMultiLineOptions,
  getMultiChartOption,
  getScatterChartOptions
} from './charts';

/**
 * Function to get the default chart type
 * @param noOfMetrics Number of metrics
 * @param noOfGroupBy Number of Group Bys
 * @returns default chart type
 */
const getDefaultChartType = (noOfMetrics = 0, noOfGroupBy = 0) => {
  switch (noOfGroupBy) {
    case 0:
      return NUMBER;
    case 1:
      return noOfMetrics === 1 ? DOUGHNUT : STACKED_VERTICAL_BAR;
    case 2:
      return noOfMetrics === 1 ? STACKED_VERTICAL_BAR : SUMMARY_TABLE;
    default:
      return null;
  }
};

const CHART_LOGOS = {
  PIE: 'PieChart.svg',
  DOUGHNUT: 'PieChart.svg',
  VERTICAL_BAR: 'BarChart.svg',
  HORIZONTAL_BAR: 'BarChart.svg',
  STACKED_HORIZONTAL_BAR: 'BarChart.svg',
  STACKED_VERTICAL_BAR: 'BarChart.svg',
  GROUPED_HORIZONTAL_BAR: 'BarChart.svg',
  GROUPED_VERTICAL_BAR: 'BarChart.svg',
  LINE: 'LineChart.svg',
  MULTILINE: 'LineChart.svg',
  STACKED_AREA: 'LineChart.svg',
  SUMMARY_TABLE: 'SummaryTable.svg',
  NUMBER: 'PieChart.svg'
};

const CHART_ICON = {
  PIE: 'PieChart',
  DOUGHNUT: 'DoughnutChart',
  VERTICAL_BAR: 'BarChart',
  HORIZONTAL_BAR: 'HorizontalBarChart',
  STACKED_HORIZONTAL_BAR: 'HorizontalBarChart',
  STACKED_VERTICAL_BAR: 'BarChart',
  GROUPED_HORIZONTAL_BAR: 'HorizontalBarChart',
  GROUPED_VERTICAL_BAR: 'BarChart',
  LINE: 'LineChart',
  MULTILINE: 'LineChart',
  STACKED_AREA: 'LineChart',
  SUMMARY_TABLE: 'SummaryTable',
  NUMBER: 'NumberChart'
};

/**
 * Method to get all the possible chart types based on metrics and group bys
 * @param noOfMetrics Number of metrics
 * @param noOfGroupBy Number of GroupBys
 * @returns List of Possible chart types
 */
const getPossibleChartTypes = (
  noOfMetrics = 0,
  noOfGroupBy = 0,
  entity = null
) => {
  const BASIC_CHARTS = [
    { name: PIE, label: 'Pie chart', icon: 'PieChart.svg' },
    { name: DOUGHNUT, label: 'Doughnut chart', icon: 'PieChart.svg' },
    { name: VERTICAL_BAR, label: 'Vertical bar chart', icon: 'BarChart.svg' },
    {
      name: HORIZONTAL_BAR,
      label: 'Horizontal bar chart',
      icon: 'BarChart.svg'
    },
    { name: LINE, label: 'Line chart', icon: 'LineChart.svg' },
    { name: SUMMARY_TABLE, label: 'Summary Table', icon: 'SummaryTable.svg' }
  ];

  const ADVANCED_CHARTS = [
    {
      name: STACKED_VERTICAL_BAR,
      label: 'Stacked vertical bar chart',
      icon: 'BarChart.svg'
    },
    {
      name: STACKED_HORIZONTAL_BAR,
      label: 'Stacked horizontal bar chart',
      icon: 'BarChart.svg'
    },
    {
      name: GROUPED_VERTICAL_BAR,
      label: 'Grouped vertical bar chart',
      icon: 'BarChart.svg'
    },
    {
      name: GROUPED_HORIZONTAL_BAR,
      label: 'Grouped horizontal bar chart',
      icon: 'BarChart.svg'
    },
    { name: MULTILINE, label: 'Multi-line chart', icon: 'LineChart.svg' },
    // { name: STACKED_AREA, label: 'Stacked area chart', icon: 'LineChart.svg' },
    { name: SUMMARY_TABLE, label: 'Summary Table', icon: 'SummaryTable.svg' }
  ];

  if (entity) {
    switch (entity) {
      case 'AssetSoftwareInventory':
        return [
          {
            name: SUMMARY_TABLE,
            label: 'Summary Table',
            icon: 'SummaryTable.svg'
          }
        ];
      default:
        break;
    }
  }

  switch (noOfGroupBy) {
    case 0:
      return [];
    case 1:
      if (noOfMetrics === 1) {
        return BASIC_CHARTS;
      }
      return ADVANCED_CHARTS;
    case 2:
      if (noOfMetrics === 1) {
        return ADVANCED_CHARTS;
      }
      return [];
    default:
      return [];
  }
};

/**
 * Transformation method for no group by criteria
 * @param chartData
 * @returns
 */
const constructMultiMetricData = (chartData) => {
  return chartData.data;
};

const constructSingleGroupByTable = (chartData) => {
  return chartData.data.map((grp) => {
    const attr = grp.hasOwnProperty('groupByUnit')
      ? `${grp.attribute}.${grp.groupByUnit}`
      : grp.attribute;
    const respObj = { [attr]: grp.label };
    grp.data.map(
      (met, ind) =>
        (respObj[met.metricId] = {
          value: met.value,
          cellType: met?.metricUnitType || null
        })
    );
    return respObj;
  });
};

const getInnerValues = (obj) => {
  const vals = obj.data.map((met) => met.label);
  const keyAttr = obj.data[0].hasOwnProperty('groupByUnit')
    ? `${obj.data[0].attribute}.${obj.data[0].groupByUnit}`
    : obj.data[0].attribute;
  const key = keyAttr;
  return { [key]: vals };
};

const constructDoubleGroupByTable = (chartData) => {
  return chartData.data.map((grp) => {
    const attribute = grp.hasOwnProperty('groupByUnit')
      ? `${grp.attribute}.${grp.groupByUnit}`
      : grp.attribute;
    let respObj = { [attribute]: grp.label };
    if (!grp.data.value && grp.data.length) {
      respObj = { ...respObj, ...getInnerValues(grp) };
    }
    const attrs = grp.data?.[0]?.data.map((met, ind) => met.metricId);
    const allMetrics = grp.data
      .map((igrp) =>
        igrp.data.map((attr, ind) => ({
          ...attr,
          attribute: attr.metricId
        }))
      )
      .flat();
    attrs.map((attr, ind) => {
      respObj[attr] = allMetrics
        .filter((met) => met.attribute === attr)
        .map((met) => ({
          value: met.value,
          cellType: met?.metricUnitType || null
        }));
    });
    return respObj;
  });
};

/**
 * Method for summary table transformation
 * @param chartData chart data
 * @returns transformed data for react-table
 */
const constructSummaryTableData = (chartData, groupByCount = 1) => {
  switch (groupByCount) {
    case 2:
      return constructDoubleGroupByTable(chartData);
    default:
      return constructSingleGroupByTable(chartData);
  }
};

/**
 * Method to get EChart transformed options for a particular chart type
 * @param type type of chart
 * @param chartData chart data
 * @returns Chart options for EChart
 */
const getChartOptions = (type, chartData, isMultiGroup = false) => {
  const types = {
    PIE: getPieOptions(chartData),
    DOUGHNUT: getPieOptions(chartData, true),
    VERTICAL_BAR: getBarOptions(chartData),
    HORIZONTAL_BAR: getBarOptions(chartData, true),
    LINE: getLineOptions(chartData),
    STACKED_VERTICAL_BAR: getStackedBarOptions(chartData, false, isMultiGroup),
    STACKED_HORIZONTAL_BAR: getStackedBarOptions(chartData, true, isMultiGroup),
    GROUPED_VERTICAL_BAR: getGroupedBarOptions(chartData, false, isMultiGroup),
    GROUPED_HORIZONTAL_BAR: getGroupedBarOptions(chartData, true, isMultiGroup),
    MULTILINE: getMultiLineOptions(chartData, false, isMultiGroup),
    STACKED_AREA: getMultiLineOptions(chartData, true, isMultiGroup),
    MULTIPLE: getMultiChartOption(chartData, false, isMultiGroup)
    // SUMMARY_TABLE: constructSummaryTableData(chartData)
  };
  return types[type] || null;
};

/**
 * Transformation method for a single group by criteria
 * @param chartData chart data
 * @param chartType type of chart
 * @returns transformed options
 */
const constructSingleGroupByChartData = (chartData, chartType, chart = {}) => {
  if (chartType === SUMMARY_TABLE) {
    return constructSummaryTableData(chartData);
  }
  const groups = chartData.data;

  const metricAttrs = groups?.[0]?.data
    .map((met) => omit(met, ['value']))
    .map((al) => ({
      ...al,
      chartType: chart?.metrics?.find((ele) => ele?.attribute === al?.attribute)
        ?.defaultChartType
    }));
  const metrics = groups.map((group) => group.data).flat();
  const metricData = metricAttrs?.map((met) => ({
    [met.attribute]: metrics
      .filter((metric) => metric.label === met.label)
      .map((metric) => metric.value)
  }));
  const groupData = groups.map((group) => omit(group, ['data']));
  const transformedObject = {
    group: groupData,
    series: metricAttrs,
    seriesData: metricData
  };
  return getChartOptions(chartType, transformedObject);
};

/**
 * Transformation method for a double group by criteria
 * @param chartData chart data
 * @param chartType type of chart
 * @returns transformed options
 */
const constructDoubleGroupByChartData = (chartData, chartType) => {
  if (chartType === SUMMARY_TABLE) {
    return constructSummaryTableData(chartData, 2);
  }
  const groups = chartData.data;
  const metricAttrs = groups?.[0]?.data
    .map((met) => omit(met, ['data', 'attribute']))
    .map((met) => ({ ...met, attribute: met.label }));
  const metrics = groups
    .map((group) =>
      group.data.map((grp) => ({
        ...grp,
        attribute: grp.data?.[0]?.attribute,
        value: grp.data?.[0]?.value
      }))
    )
    .flat();
  const metricData = metricAttrs?.map((met) => ({
    [met.label]: metrics
      .filter((metric) => metric.label === met.label)
      .map((metric) => metric.value)
  }));
  const groupData = groups.map((group) => omit(group, ['data']));
  const transformedObject = {
    group: groupData,
    series: metricAttrs,
    seriesData: metricData
  };
  return getChartOptions(chartType, transformedObject, true);
};

/**
 * Parent method to be called from any function to get the transformed chart data
 * @param data chart data
 * @param noOfMetrics number of metrics
 * @param noOfGroupBy number of groupBys
 * @param chartType chart type
 * @returns transformed chart options
 */
const getTransformedChartData = (
  data,
  noOfMetrics = 0,
  noOfGroupBy = 0,
  chartType,
  chart = {}
) => {
  if (!data) {
    return null;
  }
  if (data?.data?.length === 0) {
    return null;
  }
  switch (noOfGroupBy) {
    case 0:
      return constructMultiMetricData(data);
    case 1:
      return noOfMetrics === 1
        ? constructSingleGroupByChartData(data, chartType || PIE)
        : constructSingleGroupByChartData(
            data,
            chartType || STACKED_VERTICAL_BAR,
            chart
          );
    case 2:
      return noOfMetrics === 1
        ? constructDoubleGroupByChartData(
            data,
            chartType || STACKED_VERTICAL_BAR
          )
        : constructSummaryTableData(data, noOfGroupBy);
    default:
      return null;
  }
};

const getOptions = (data, intl, type, config = {}) => {
  if (config?.customChart) {
    if (type === 'SCATTER') {
      return getScatterChartOptions({
        data
      });
    }
    return getCustomChartOptions({
      group: data?.group,
      chartSeriesData: data?.series,
      config: config,
      type: type
    });
  }
  return getLineOptions({
    group: Object.keys(data).map((el) => ({
      label: getTranslatedText(intl, el)
    })),
    series: [{ rawData: Object.values(data) }],
    seriesData: [],
    isEfficiencyReport: true
  });
};

const getTransformedData = (data = {}, intl, chartType = 'LINE', config) => {
  if (isEmpty(data)) return null;
  if (!isEmpty(data)) {
    return getOptions(data, intl, chartType, config);
  }
  return null;
};

export {
  getDefaultChartType,
  getPossibleChartTypes,
  getTransformedChartData,
  getTransformedData,
  CHART_LOGOS,
  CHART_ICON
};
